import { FeatureToggles } from '@/features/feature-toggles'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type Member } from '@/features/iam/members/Member'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { links } from '@/utils/link'
import { syncWorkspaceFilter } from './sync-workspace-filter'
import { action, computed, observable } from 'mobx'
import { merge, type Observable } from 'rxjs'
import { distinctUntilChanged, filter, find, map, tap } from 'rxjs/operators'
import type { Id } from '@/infra/id'
import type { NavigateFn } from '@/RootStore'
import type { SystemNotification } from '@/features/notifications'
import { toStream } from '@/utils/mobx-util'
import { tailMap } from '@/utils/rx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { filterIncomingOpportunities } from './filter-incoming-opportunities'
import { OpportunitiesSearchViewModel } from './OpportunitiesSearchViewModel'
import { type Opportunity } from './Opportunity'
import { OpportunityDetailsViewModel } from './OpportunityDetailsViewModel'
import { OpportunityViewModel } from './OpportunityViewModel'
import {
  type ListOpportunitiesFn,
  type OpportunityStore,
} from './OpportunityStore'
import { validate as isValidUUID } from 'uuid'
import { OverachieverDialogViewModel } from '@/features/opportunities/OverachieverDialog/OverachieverDialogViewModel'
import { orderBy, sum } from 'lodash-es'
import { type JobEvent, type JobEventType } from '@/features/jobs/JobEvent'
import { type ProOpenJobCount } from '@/features/jobs/dashboard/job-stats/pro-open-job-count'
import type {
  FetchBookedConsultationsFn,
  FetchProOpenJobCountsFn,
  PickupConsultationJobFn,
} from '@/features/jobs'
import type {
  FetchUserDataFn,
  ListWorkspaceMembershipsFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetTeamMembersForWorkspaceMemberFn,
  GetUserDataFn,
} from '@/features/iam'
import { type ConsultationModalViewModel } from '@/features/opportunities/ConsultationModal/ConsultationModalViewModel'

/**
 * Opportunities view model.
 */
export class OpportunitiesViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Fetcher for opportunities.
   */
  private readonly _fetchOpportunities: ListOpportunitiesFn = (query, signal) =>
    this._opportunityStore.listOpportunities(
      {
        ...query,
        orderBy: { Transmitted: 'asc' },
      },
      signal,
    )

  /**
   * View models for the listed opportunities.
   */
  private readonly _opportunityVms: ViewModelList<
    Opportunity,
    OpportunityViewModel
  >

  /**
   * Whether the Opportunities screen is currently the active page.
   * This allows us to not show the global slide-out when viewing the page.
   */
  @observable
  accessor isActive = false

  /**
   * User open jobs count.
   */
  @observable
  accessor proOpenJobCounts = new Map<Id, number>()

  /**
   * Opportunity searcher.
   */
  readonly searcher

  /**
   * Details view model.
   */
  readonly detailsVm

  /**
   * Dialog for overachiever modal.
   */
  readonly overachieverModalVm

  /**
   * When an Opportunity becomes available for pickup,
   * show a notification that, when clicked, selects the Opportunity.
   */
  readonly systemNotifications$

  constructor(
    private readonly _opportunityStore: OpportunityStore,
    private readonly _fetchBookedConsultations: FetchBookedConsultationsFn,
    private readonly _pickupConsultationJob: PickupConsultationJobFn,
    private readonly _fetchProOpenJobCounts: FetchProOpenJobCountsFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _listWorkspaceMemberships: ListWorkspaceMembershipsFn,
    private readonly _getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn,
    private readonly _fetchUserData: FetchUserDataFn,
    private readonly _getUserData: GetUserDataFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _navigate: NavigateFn,
    private readonly _jobEvents$: Observable<JobEvent>,
    currentMember$: Observable<Member>,
    private readonly _consultationModalVm: ConsultationModalViewModel,
  ) {
    this.systemNotifications$ = this._opportunityStore.events$.pipe(
      filterIncomingOpportunities(),
      map((op): SystemNotification => {
        const removed$ = this._opportunityStore.events$.pipe(
          find((e) => e.type === 'OpportunityRemoved' && e.opportunity === op),
        )
        return {
          icon: op.workspace.branding.logos.icon,
          // Since we auto-close the notification when the opportunity is removed anyway,
          // we can set a high timeout.
          timeout: 999999999,
          title: `New Opportunity Available!`,
          body: `[${op.workspace.name}] ${op.name || 'No description'}`,
          close$: removed$,
          onClick: () =>
            this.detailsVm.select(this._getOpportunityViewModel(op)),
        }
      }),
    )

    this.searcher = new OpportunitiesSearchViewModel(
      this._fetchOpportunities,
      this._opportunityStore.events$,
      this._createAlertRetryWrapper,
    )

    this.detailsVm = new OpportunityDetailsViewModel(
      this._opportunityStore,
      this._fetchBookedConsultations,
      this._pickupConsultationJob,
      this._newAlert,
      this._createAlertErrorHandler,
      this._navigate,
      this._getCurrentMember,
      this._canPickUpOpportunityAfterMemberDataRefresh.bind(this),
      this._consultationModalVm,
    )

    this.overachieverModalVm = new OverachieverDialogViewModel(
      this._navigate,
      () => this.globalJobCapacity,
    )

    this._opportunityVms = new ViewModelList({
      models: () => this.searcher.scroller.items.slice(),
      create: (op) => this._createOpportunityViewModel(op),
    })

    this._lifecycle
      .manage(syncWorkspaceFilter(this.searcher, currentMember$))
      .manage(this.searcher)
      .manage(this._opportunityVms)
      .manage(this._refreshJobCountsOnChange())
      // Whenever we observe a change to our ability to pick up jobs
      // at the global level, display the overachiever dialog once.
      .manage(
        toStream(() => this.canPickUpOpportunityGlobally, true).pipe(
          distinctUntilChanged(),
          tap((canPickupGlobally) =>
            canPickupGlobally
              ? this.overachieverModalVm.modal.close()
              : this.overachieverModalVm.showOnce(),
          ),
        ),
      )
  }

  /**
   * Computes whether an active PTIN is required for the current member.
   *
   * @returns {boolean} Returns true if an active PTIN is required, otherwise false.
   */
  @computed
  get ptinRequired(): boolean {
    const featureEnabled = FeatureToggles.requireActivePtin()
    if (!featureEnabled) {
      return false
    }

    const userData = this._getUserData(this.currentMember.user.publicId)

    return (
      userData?.userDataType === 'Pro' && userData?.ptinStatus === 'Inactive'
    )
  }

  /**
   * The signed-in member.
   */
  @computed
  get currentMember() {
    return this._getCurrentMember()
  }

  /**
   * Available opportunities.
   */
  @computed
  get opportunities() {
    return this._opportunityVms.items
  }

  /**
   * Whether list items show which workspace they belong to.
   */
  @computed
  get showWorkspaceInfo() {
    return (
      this.searcher.workspaceFilter.type === 'AllWorkspaces' &&
      this.workspacesWhereUserCanViewOpportunities.length > 1
    )
  }

  /**
   * Whether to show the banner about reaching the pickup limit.
   */
  @computed
  get showOverachieverBanner() {
    return !this.canPickUp || this.workspacesWithoutPickUpCapacity.length > 0
  }

  /**
   * Whether the list is empty after loading everything.
   */
  @computed
  get showEmptyState() {
    return (
      !this.searcher.scroller.fetch.pending &&
      this.opportunities.length === 0 &&
      this.searcher.scroller.isDone
    )
  }

  /**
   * Whether there are no opportunities at all, as opposed to none matching
   * the filters. The length check covers realtime additions since the search.
   */
  @computed
  get isCompletelyEmpty() {
    return (
      this.searcher.searchState === 'CompletelyEmpty' &&
      this.opportunities.length === 0
    )
  }

  /**
   * How many open jobs the user may have across all workspaces.
   */
  @computed
  get globalJobCapacity() {
    return this._getUserData(this.currentMember.user.publicId)?.jobCapacity ?? 0
  }

  /**
   * Workspace memberships.
   */
  @computed
  get workspaceMemberships() {
    return this._getCurrentUserWorkspaceMemberships()
  }

  /**
   * Total Open jobs
   */
  @computed
  get totalOpenJobs() {
    return sum(Array.from(this.proOpenJobCounts.values()))
  }

  /**
   * Workspaces where the capacity has been reached or exceeded.
   */
  @computed
  get workspacesWithoutPickUpCapacity() {
    return this.workspaceMemberships
      .filter((x) => {
        const openJobCount = this.proOpenJobCounts.get(x.workspace.id)
        const totalOpenJobs = openJobCount ?? 0

        return (
          x.member.maxConcurrentProviderJobs !== 0 &&
          totalOpenJobs >= x.member.maxConcurrentProviderJobs
        )
      })
      .map((x) => x.workspace)
  }

  /**
   * Whether we can pick up opportunities in the currently selected workspace.
   */
  @computed
  get canPickUpOpportunitiesInCurrentWorkspace() {
    const workspaceFilter = this.searcher.workspaceFilter
    if (workspaceFilter.type === 'AllWorkspaces') {
      return true
    }

    return !this.workspacesWithoutPickUpCapacity.some(
      (x) => x.id === workspaceFilter.workspace.id,
    )
  }

  /**
   * Whether we can pick up an opportunity at all.
   */
  @computed
  get canPickUp() {
    return (
      this.canPickUpOpportunityGlobally &&
      this.canPickUpOpportunitiesInCurrentWorkspace
    )
  }

  /**
   * The workspaces where the user, or one of their teams, may view
   * opportunities, by name.
   */
  @computed
  get workspacesWhereUserCanViewOpportunities() {
    return orderBy(
      this._getCurrentUserWorkspaceMemberships()
        .filter(({ member, workspace }) => {
          return (
            member.permissions.viewOpportunities ||
            this._getTeamMembersForWorkspaceMember(workspace.id).some(
              (tm) => tm.permissions.viewOpportunities,
            )
          )
        })
        .map((m) => m.workspace),
      (ws) => ws.name.toLowerCase(),
      'asc',
    )
  }

  /**
   * Which workspaces the pickup limit applies to, for the overachiever
   * banner; `null` when it shouldn't be qualified.
   */
  @computed
  get pickupLimitWorkspaceText() {
    if (!this.canPickUpOpportunityGlobally) {
      return null
    }

    const workspaceFilter = this.searcher.workspaceFilter
    // When the filtered workspace is at its limit, the banner is about it alone.
    if (
      workspaceFilter.type === 'SpecificWorkspace' &&
      this.workspacesWithoutPickUpCapacity.some(
        (x) => x.id === workspaceFilter.workspace.id,
      )
    ) {
      return null
    }

    const count = this.workspacesWithoutPickUpCapacity.length
    return count === 1
      ? `in the ${this.workspacesWithoutPickUpCapacity[0].name} workspace`
      : `across ${count} workspaces`
  }

  /**
   * Whether the user can pick up opportunity globally
   */
  @computed
  get canPickUpOpportunityGlobally() {
    return (
      this.globalJobCapacity === 0 ||
      this.totalOpenJobs < this.globalJobCapacity
    )
  }

  /** @inheritDoc */
  @action.bound
  activate() {
    this.isActive = true
    // Ensure we always reset to All Opportunities on load.
    this.searcher.selectWorkspaceForFiltering(Workspace.none)
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    // Deselect any selected op when leaving the Ops view.
    this.detailsVm.deselect()
    this.isActive = false
    // Deactivating the searcher reloads Ops on every visit, so we don't miss
    // any that realtime dropped while the view was closed.
    this._lifecycle.deactivate()
  }

  /**
   * Views an opportunity by ID.
   *
   * @param opportunityId
   * @param signal
   */
  async viewOpportunity(opportunityId: Id, signal?: AbortSignal) {
    if (!opportunityId || !isValidUUID(opportunityId)) {
      this._newAlert()
        .withState('warning')
        .autoDismiss()
        .saying('Uh-oh', 'That was not a valid opportunity ID')
        .show()

      this._navigate(links.opportunities())

      return
    }

    try {
      const withRetry = this._createAlertRetryWrapper(
        'Unable to load the opportunity',
        signal,
      )
      const opportunity = await withRetry(() =>
        this._opportunityStore.fetchOpportunity(opportunityId, signal),
      )
      if (!opportunity) {
        this._newAlert()
          .withState('warning')
          .autoDismiss()
          .saying(
            'The opportunity has vanished',
            'The opportunity you are trying to view is no longer available',
          )
          .show()

        this._navigate(links.opportunities())
        return
      }

      this.detailsVm.select(this._getOpportunityViewModel(opportunity))
    } catch {
      // The retry alert has reported the failure; the list stays usable with
      // nothing selected.
    }
  }

  /**
   * Whether the user can pick up an opportunity.
   *
   * @param opportunity
   */
  canPickUpOpportunity(opportunity: Opportunity) {
    return (
      this.canPickUpOpportunityGlobally &&
      !this.workspacesWithoutPickUpCapacity.some(
        (x) => x.id === opportunity.workspace.id,
      )
    )
  }

  /**
   * Creates a pipeline that will refresh the job counts based on
   * the workspace filter + any time we observe a status change for any job.
   *
   * @private
   */
  private _refreshJobCountsOnChange(): Observable<unknown> {
    // The event types that _may_ result in a status change.
    const eventTypes: JobEventType[] = [
      'Reopened',
      'Claimed',
      'Closed',
      'Transferred',
      'Forfeited',
      'Resumed',
      'PutOnHold',
      'Unclaimed',
      'ProviderAdded',
      'ProviderRemoved',
    ]

    return merge(
      // When the workspace filter changes (including the initial value)
      this.searcher.workspaceFilter$,
      // Or if we observe an event that may have changed the status
      this._jobEvents$.pipe(filter((e) => eventTypes.includes(e.eventType))),
    ).pipe(
      // Ensures that we fetch sequentially, and that we only do it for
      // the last one if one is currently running. A failed fetch keeps the
      // counts we have.
      tailMap(() =>
        this._fetchOpenJobCountsOf(this.currentMember).catch(() => undefined),
      ),
    )
  }

  /**
   * Fetches the open job counts for the specified member.
   *
   * @private
   */
  private async _fetchOpenJobCountsOf(member: Member) {
    return this._fetchProOpenJobCounts(member.user.publicId).then(
      this._setOpenJobCounts,
    )
  }

  /**
   * Sets the open job counts.
   *
   * @private
   */
  @action.bound
  private _setOpenJobCounts(proOpenJobCounts: ProOpenJobCount[]) {
    proOpenJobCounts.forEach((x) =>
      this.proOpenJobCounts.set(x.workspaceId, x.total),
    )
  }

  /**
   * Whether the user can pick up an opportunity, refreshing their user data
   * and memberships first.
   *
   * @param opportunity
   */
  private async _canPickUpOpportunityAfterMemberDataRefresh(
    opportunity: Opportunity,
  ) {
    await Promise.all([
      this._fetchUserData(this.currentMember.user.publicId, {
        skipCache: true,
      }),
      this._listWorkspaceMemberships({ skipCache: true }),
    ])

    return this.canPickUpOpportunity(opportunity)
  }

  /**
   * Gets the listed view model for the opportunity, or a new one when the
   * opportunity isn't listed (e.g. opened from a link or a notification).
   *
   * @param opportunity
   */
  private _getOpportunityViewModel(opportunity: Opportunity) {
    return (
      this._opportunityVms.get(opportunity) ??
      this._createOpportunityViewModel(opportunity)
    )
  }

  /**
   * Creates a view model for the opportunity.
   *
   * @param opportunity
   */
  private _createOpportunityViewModel(opportunity: Opportunity) {
    return new OpportunityViewModel(
      opportunity,
      this._getCurrentUserWorkspaceMemberships,
      () => this.detailsVm.pickup.pending,
      (op) => this.canPickUpOpportunity(op),
      () => this.detailsVm.selected,
      this.detailsVm.select,
    )
  }
}
